import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Checkbox } from "../../../../components/ui/common/checkbox";
import { Input } from "../../../../components/ui/common/input";
import { cn } from "../../../../lib/utils";
import { FilterOption } from "../types/ingredientListView.types";

interface FilterSearchMultiSelectProps {
  label: string;
  placeholder?: string;
  options: FilterOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;
  noResultsText?: string;
}

/**
 * One multi-select group inside the filter panel (ingredient / supplier /
 * data-type) — a search-as-you-type dropdown, styled after
 * `FormSearchSelect` (src/components/ui/Input/FormSearchSelect/FormSearchSelect.tsx)
 * but standalone (no react-hook-form `control`, since filter selections are
 * plain `string[]` from `useIngredientFilterState`) and multi-select: picking
 * a row toggles it and keeps the dropdown open for further selections,
 * matching the legacy `react-select isMulti` behaviour
 * (see src/screens/ingredients/ingredientList/IngredientFilter.tsx).
 */
const FilterSearchMultiSelect = ({
  label,
  placeholder = "Search...",
  options,
  selectedValues,
  onToggle,
  noResultsText = "No results found"
}: FilterSearchMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = searchQuery
    ? options.filter(option => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : options;

  const summaryText =
    selectedValues.length === 0
      ? ""
      : selectedValues.length === 1
        ? options.find(option => option.value === selectedValues[0])?.label || ""
        : `${selectedValues.length} selected`;

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <p className="tw-text-sm tw-font-medium tw-text-foreground">{label}</p>
      <div ref={containerRef} className="tw-relative tw-w-full">
        <div
          className={cn(
            "tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-1.5 tw-text-sm tw-shadow-xs tw-transition-colors tw-cursor-pointer",
            "focus-within:tw-border-ring focus-within:tw-ring-ring/50 focus-within:tw-ring-[3px]"
          )}
          onClick={() => setOpen(prev => !prev)}
        >
          <Input
            value={open ? searchQuery : summaryText}
            onChange={e => {
              setSearchQuery(e.target.value);
              setOpen(true);
            }}
            placeholder={placeholder}
            onClick={e => {
              // Stop the wrapper's own onClick from also toggling `open` —
              // this click should only ever open the dropdown, not close it.
              e.stopPropagation();
              if (!open) {
                setOpen(true);
                setSearchQuery("");
              }
            }}
            className="tw-border-0 tw-shadow-none focus-visible:tw-ring-0 tw-h-auto tw-px-2.5 tw-bg-white"
          />
          <ChevronDown
            className={cn(
              "tw-mr-2.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-muted-foreground tw-transition-transform",
              open && "tw-rotate-180"
            )}
          />
        </div>

        {open && (
          <div className="tw-absolute tw-z-50 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-md tw-border tw-border-border tw-bg-popover tw-text-popover-foreground tw-shadow-md">
            {filteredOptions.length === 0 ? (
              <div className="tw-p-4 tw-text-center tw-text-sm tw-text-muted-foreground">
                {noResultsText}
              </div>
            ) : (
              <div className="tw-p-1">
                {filteredOptions.map(option => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      role="option"
                      aria-selected={isSelected}
                      className={cn(
                        "tw-flex tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-rounded-sm tw-px-2.5 tw-py-2 tw-text-sm tw-text-foreground tw-outline-none tw-transition-colors hover:tw-bg-primary/15",
                        isSelected && "tw-bg-accent/50"
                      )}
                      onClick={() => onToggle(option.value)}
                    >
                      <Checkbox checked={isSelected} className="tw-pointer-events-none" />
                      <span className="tw-flex-1">{option.label}</span>
                      {isSelected && <Check className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-primary" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSearchMultiSelect;
